import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Reusable Tooltip component
 * Usage: <Tooltip text="Tooltip text">Child element</Tooltip>
 * 
 * @param {string} text - The tooltip text to display
 * @param {string} title - Optional title for the tooltip
 * @param {string} color - Optional color for the title (default: #4a90e2)
 * @param {React.ReactNode} children - The element to attach the tooltip to
 * @param {string} wrapperStyle - Optional style for the wrapper div
 * @param {string} placement - Optional forced placement: 'top', 'bottom', 'left', 'right' (default: auto)
 */
const Tooltip = ({ text, title, color = '#4a90e2', children, wrapperStyle = {}, placement = 'auto' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [arrowStyle, setArrowStyle] = useState({});
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);

  const updatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 10;
    const gap = 10;

    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const triggerLeft = triggerRect.left;
    const triggerRight = triggerRect.right;
    const triggerTop = triggerRect.top;
    const triggerBottom = triggerRect.bottom;

    // Check available space
    const spaceAbove = triggerTop;
    const spaceBelow = viewportHeight - triggerBottom;
    const spaceLeft = triggerLeft;
    const spaceRight = viewportWidth - triggerRight;
    const tooltipHeight = tooltipRect.height;
    const tooltipWidth = tooltipRect.width;

    // Decide placement based on prop or auto-detect
    let calculatedPlacement = placement === 'auto' ? 'top' : placement;
    let top, left;
    
    if (placement === 'auto') {
      // Auto-detect best placement
      if (spaceAbove >= tooltipHeight + gap + padding) {
        calculatedPlacement = 'top';
        top = triggerTop - gap;
      } else if (spaceBelow >= tooltipHeight + gap + padding) {
        calculatedPlacement = 'bottom';
        top = triggerBottom + gap;
      } else {
        calculatedPlacement = spaceAbove > spaceBelow ? 'top' : 'bottom';
        top = calculatedPlacement === 'top' ? padding : viewportHeight - padding - tooltipHeight;
      }
    } else if (placement === 'left') {
      calculatedPlacement = 'left';
      left = triggerLeft - gap;
      top = triggerTop + (triggerRect.height / 2);
    } else if (placement === 'right') {
      calculatedPlacement = 'right';
      left = triggerRight + gap;
      top = triggerTop + (triggerRect.height / 2);
    } else if (placement === 'top') {
      calculatedPlacement = 'top';
      top = triggerTop - gap;
    } else if (placement === 'bottom') {
      calculatedPlacement = 'bottom';
      top = triggerBottom + gap;
    }

    // Calculate final position based on placement
    let finalLeft, finalTop;
    let transformX = '0%';
    let transformY = '0%';

    if (calculatedPlacement === 'left' || calculatedPlacement === 'right') {
      // Horizontal placement (left or right)
      finalLeft = left;
      finalTop = top;
      transformY = '-50%';
      if (calculatedPlacement === 'left') {
        transformX = '-100%';
      }
      
      // Adjust if tooltip goes off screen vertically
      const tooltipTopEdge = finalTop - (tooltipHeight / 2);
      const tooltipBottomEdge = finalTop + (tooltipHeight / 2);
      if (tooltipTopEdge < padding) {
        finalTop = padding + (tooltipHeight / 2);
      } else if (tooltipBottomEdge > viewportHeight - padding) {
        finalTop = viewportHeight - padding - (tooltipHeight / 2);
      }
      
      // Adjust if tooltip goes off screen horizontally
      if (calculatedPlacement === 'left' && finalLeft - tooltipWidth < padding) {
        finalLeft = padding + tooltipWidth;
      } else if (calculatedPlacement === 'right' && finalLeft + tooltipWidth > viewportWidth - padding) {
        finalLeft = viewportWidth - padding - tooltipWidth;
      }
    } else {
      // Vertical placement (top or bottom)
      finalTop = top;
      // Calculate horizontal position - center on trigger by default
      finalLeft = triggerCenterX;
      transformX = '-50%';
      const halfWidth = tooltipWidth / 2;

      // Calculate where tooltip would be if centered
      const centeredLeft = triggerCenterX - halfWidth;
      const centeredRight = triggerCenterX + halfWidth;

      // Check if tooltip would go off screen and adjust
      if (centeredLeft < padding && centeredRight > viewportWidth - padding) {
        finalLeft = viewportWidth / 2;
        transformX = '-50%';
      } else if (centeredLeft < padding) {
        finalLeft = padding;
        transformX = '0%';
      } else if (centeredRight > viewportWidth - padding) {
        finalLeft = viewportWidth - padding;
        transformX = '-100%';
      }
      
      transformY = calculatedPlacement === 'top' ? '-100%' : '0%';
    }

    setTooltipStyle({
      position: 'fixed',
      top: `${finalTop}px`,
      left: `${finalLeft}px`,
      transform: `translate(${transformX}, ${transformY})`,
      padding: '10px 12px',
      backgroundColor: '#333',
      color: '#fff',
      borderRadius: '6px',
      fontSize: '12px',
      zIndex: 10000,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
      pointerEvents: 'none',
      minWidth: '200px',
      maxWidth: '300px',
      whiteSpace: 'normal'
    });

    // Set arrow style based on placement
    if (calculatedPlacement === 'left' || calculatedPlacement === 'right') {
      // Arrow on left or right side
      const triggerCenterY = triggerTop + (triggerRect.height / 2);
      const tooltipActualTop = finalTop - (tooltipHeight / 2);
      const triggerCenterRelativeToTooltip = triggerCenterY - tooltipActualTop;
      const arrowPercent = (triggerCenterRelativeToTooltip / tooltipHeight) * 100;
      const arrowTopPercent = `${Math.max(10, Math.min(90, arrowPercent))}%`;
      
      setArrowStyle({
        position: 'absolute',
        // For left placement, arrow on right side of tooltip (use right: 0, then translate to move outside)
        // For right placement, arrow on left side of tooltip (use left: 0, then translate to move outside)
        [calculatedPlacement === 'left' ? 'right' : 'left']: 0,
        top: arrowTopPercent,
        transform: `translateY(-50%) translateX(${calculatedPlacement === 'left' ? '100%' : '-100%'})`,
        width: 0,
        height: 0,
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
        // For left placement, arrow is on right side of tooltip pointing right (towards badge) - use borderLeft
        // For right placement, arrow is on left side of tooltip pointing left (towards badge) - use borderRight
        [calculatedPlacement === 'left' ? 'borderLeft' : 'borderRight']: '6px solid #333'
      });
    } else {
      // Arrow on top or bottom
      // Calculate tooltip's actual left edge based on transform
      // When transformX is '-50%', finalLeft is center, so left = center - width/2
      // When transformX is '0%', finalLeft is left edge, so left = finalLeft
      // When transformX is '-100%', finalLeft is right edge, so left = finalLeft - width
      const tooltipActualLeft = finalLeft - (tooltipWidth * (transformX === '-50%' ? 0.5 : transformX === '0%' ? 0 : 1));
      const triggerCenterRelativeToTooltip = triggerCenterX - tooltipActualLeft;
      const arrowPercent = (triggerCenterRelativeToTooltip / tooltipWidth) * 100;
      const arrowLeftPercent = `${Math.max(10, Math.min(90, arrowPercent))}%`;
      
      setArrowStyle({
        position: 'absolute',
        [calculatedPlacement === 'top' ? 'top' : 'bottom']: '100%',
        left: arrowLeftPercent,
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        [calculatedPlacement === 'top' ? 'borderTop' : 'borderBottom']: '6px solid #333'
      });
    }
  };

  const handleMouseEnter = (e) => {
    triggerRef.current = e.currentTarget;
    const triggerRect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 10;
    const gap = 10;
    
    // Estimate tooltip dimensions for initial positioning
    const estimatedHeight = title ? 70 : 50;
    const estimatedWidth = Math.min(300, Math.max(200, (text.length || 20) * 7));
    
    // Calculate initial position based on placement preference
    let initialTop, initialLeft;
    let initialTransformX = '0%';
    let initialTransformY = '0%';
    let initialPlacement = placement === 'auto' ? 'top' : placement;
    
    if (placement === 'left') {
      initialPlacement = 'left';
      initialLeft = triggerRect.left - gap;
      initialTop = triggerRect.top + (triggerRect.height / 2);
      initialTransformX = '-100%';
      initialTransformY = '-50%';
    } else if (placement === 'right') {
      initialPlacement = 'right';
      initialLeft = triggerRect.right + gap;
      initialTop = triggerRect.top + (triggerRect.height / 2);
      initialTransformY = '-50%';
    } else {
      // Vertical placement (top/bottom/auto)
      const spaceAbove = triggerRect.top;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const placeAbove = placement === 'auto' 
        ? (spaceAbove >= estimatedHeight + gap + padding || spaceBelow < estimatedHeight + gap + padding)
        : (placement === 'top');
      
      if (placement === 'auto') {
        initialPlacement = placeAbove ? 'top' : 'bottom';
      }
      
      initialTop = placeAbove ? triggerRect.top - gap : triggerRect.bottom + gap;
      
      // Calculate initial horizontal position
      const triggerCenterX = triggerRect.left + triggerRect.width / 2;
      initialLeft = triggerCenterX;
      initialTransformX = '-50%';
      const halfWidth = estimatedWidth / 2;
      
      // Calculate where tooltip would be if centered
      const centeredLeft = triggerCenterX - halfWidth;
      const centeredRight = triggerCenterX + halfWidth;
      
      // Check if tooltip would go off screen and adjust
      if (centeredLeft < padding && centeredRight > viewportWidth - padding) {
        initialLeft = viewportWidth / 2;
        initialTransformX = '-50%';
      } else if (centeredLeft < padding) {
        initialLeft = padding;
        initialTransformX = '0%';
      } else if (centeredRight > viewportWidth - padding) {
        initialLeft = viewportWidth - padding;
        initialTransformX = '-100%';
      }
      
      initialTransformY = placeAbove ? '-100%' : '0%';
    }
    
    // Set initial position immediately so tooltip appears in correct place
    setTooltipStyle({
      position: 'fixed',
      top: `${initialTop}px`,
      left: `${initialLeft}px`,
      transform: `translate(${initialTransformX}, ${initialTransformY})`,
      padding: '10px 12px',
      backgroundColor: '#333',
      color: '#fff',
      borderRadius: '6px',
      fontSize: '12px',
      zIndex: 10000,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
      pointerEvents: 'none',
      minWidth: '200px',
      maxWidth: '300px',
      whiteSpace: 'normal'
    });

    // Set initial arrow style
    if (initialPlacement === 'left' || initialPlacement === 'right') {
      setArrowStyle({
        position: 'absolute',
        // For left placement, arrow on right side of tooltip (use right: 0, then translate to move outside)
        // For right placement, arrow on left side of tooltip (use left: 0, then translate to move outside)
        [initialPlacement === 'left' ? 'right' : 'left']: 0,
        top: '50%',
        transform: `translateY(-50%) translateX(${initialPlacement === 'left' ? '100%' : '-100%'})`,
        width: 0,
        height: 0,
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
        [initialPlacement === 'left' ? 'borderLeft' : 'borderRight']: '6px solid #333'
      });
    } else {
      setArrowStyle({
        position: 'absolute',
        [initialPlacement === 'top' ? 'top' : 'bottom']: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        [initialPlacement === 'top' ? 'borderTop' : 'borderBottom']: '6px solid #333'
      });
    }
    
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
    triggerRef.current = null;
  };

  // Refine position after tooltip renders with actual dimensions
  useEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current) {
      // After render, update position with actual dimensions
      requestAnimationFrame(() => {
        setTimeout(() => {
          updatePosition();
        }, 0);
      });
    }
  }, [isVisible]);

  // Update position on scroll and resize
  useEffect(() => {
    if (isVisible) {
      const handleUpdate = () => {
        updatePosition();
      };
      
      window.addEventListener('resize', handleUpdate);
      window.addEventListener('scroll', handleUpdate, true);
      
      return () => {
        window.removeEventListener('resize', handleUpdate);
        window.removeEventListener('scroll', handleUpdate, true);
      };
    }
  }, [isVisible]);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ display: 'inline-block', ...wrapperStyle }}
      >
        {children}
      </div>
      {isVisible && text && createPortal(
        <div
          ref={tooltipRef}
          style={tooltipStyle}
        >
          {title && (
            <div style={{ fontWeight: '600', marginBottom: '4px', color: color }}>
              {title}
            </div>
          )}
          <div style={{ fontSize: title ? '11px' : '12px', color: title ? '#ccc' : '#fff' }}>
            {text}
          </div>
          {/* Arrow */}
          <div style={arrowStyle} />
        </div>,
        document.body
      )}
    </>
  );
};

export default Tooltip;
